<template>
  <div class="box-header">
    <SmartSearch v-model:form="searchFrom" ref="search" @search="searchFun" @reset="resetFun" :option="searchOption" :showSearch="showSearch">
      <template #menuLeft>
        <template v-if="props.option.addBtn !== false">
          <smart-button class="margin-right" icon="icon-zengjia" type="primary" @click="onCreate">
            {{ lang('新增') }}
          </smart-button>
        </template>
        <slot name="menuLeft" />
      </template>
    </SmartSearch>
  </div>
  <SmartTable
    ref="tableRef"
    :loading="dataMap.loading"
    :columns="pageColumn"
    :dataSource="dataList"
    @change="pageChange"
    :total="dataMap.total"
    :current="dataMap.current"
    :pageSize="dataMap.pageSize"
    :rowSelection="rowSelection"
    :rowKey="rowKey"
    :showIndex="showIndex"
    :selectRows="selectedRowKeys"
    :option="props.option"
  ></SmartTable>
  <SmartModalForm ref="modalForm" :option="props.option" @success="modalFormSuccess" :fromLayout="fromLayout" />
  <SmartModalDetail ref="modalDetail" :option="props.option" />
</template>

<script lang="tsx" setup name="SmartCurd">
import { onMounted, reactive, ref, watchEffect, inject } from 'vue'
import { message, Modal } from 'ant-design-vue'
import SmartButton from '../smart-button/index.vue'
import SmartSearch from '../smart-search/index.vue'
import SmartTable from '../smart-table/index.vue'
import SmartModalForm from '../smart-modal-form/index.vue'
import SmartModalDetail from '../smart-modal-detail/index.vue'
import SmartButtonList from '../smart-button-list/index.vue'
import { filterPageOption, filterSearchOption } from './utils'
import type { Values } from '../../common'
const lang = inject('lang')

const searchFrom = defineModel('searchFrom', {
  type: Object,
  default: () => reactive({})
})

const emit = defineEmits(['customCreate', 'success', 'customEdit', 'customDelete', 'selectionChange', 'searchReset'])
const props = defineProps({
  option: {
    type: Object,
    required: true
  }
})

const tableRef = ref()
const rowKey = ref('id')
const modalForm = ref()
const modalDetail = ref()
const search = ref()
const showSearch = ref(false)
const showIndex = ref(false)
const fromLayout = ref('vertical')

const rowSelection = ref(null)

const selectedRowKeys = ref([])

if (typeof props.option !== 'object') {
  message.warn('props.option必须是object')
  throw new Error('props.option必须是object')
}

if (props.option.fromLayout) {
  fromLayout.value = props.option.fromLayout
}

if (props.option.search !== false) {
  showSearch.value = true
}

if (props.option.index !== false) {
  showIndex.value = true
}

const API = props?.option?.API || {}

if (!API.query) {
  message.warn('请配置页面请求')
  throw new Error('请配置页面请求')
}

if (!props.option.column) {
  message.warn('请配置column')
  throw new Error('请配置column')
}

if (props.option.selection) {
  if (props.option.selection === true) {
    rowSelection.value = {
      selectedRowKeys: selectedRowKeys,
      onChange: (keys: string[], selectedRows: Record<any, any>[]) => {
        selectedRowKeys.value = keys
        emit('selectionChange', selectedRows, keys)
      }
    }
  }

  if (typeof props.option.selection === 'object') {
    rowSelection.value = props.option.selection
  }
}

const clearSelect = () => {
  selectedRowKeys.value = []
  emit('selectionChange', [], undefined)
}

if (props.option.rowKey) {
  rowKey.value = props.option.rowKey
}

const pageColumn = ref<Values[]>([])
watchEffect(() => {
  const tableColumn = filterPageOption(props.option.column, lang)
  if (props.option.menu !== false) {
    tableColumn.push({
      title: lang('操作'),
      width: props.option.menuWidth || 180,
      fixed: 'right',
      dataIndex: 'option',
      key: 'option',
      display: false,
      align: 'center',
      render({ text, record, index }: any) {
        const btn: Record<any, any> = (props.option.btnsVisible && props.option.btnsVisible(record)) || {}
        const mapOption = [
          {
            label: lang('查看'),
            type: 'view',
            display: btn.view !== false && props.option.viewBtn !== false,
            click: () => onView(record)
          },
          {
            label: lang('编辑'),
            type: 'edit',
            display: btn.edit !== false && props.option.editBtn !== false,
            click: () => onUpdate(record)
          },
          {
            label: lang('删除'),
            type: 'delete',
            display: btn.delete !== false && props.option.delBtn !== false,
            click: () => onDelete(record)
          },
            ...props?.option?.menuOption( record, index) || []
        ]
        return <SmartButtonList options={mapOption} />
      }
    })
  }
  pageColumn.value = tableColumn
})

const searchOption = ref<Values[]>([])

watchEffect(() => {
  searchOption.value = filterSearchOption(props.option.column, lang)
})

const dataList = ref([])

const dataMap = reactive({
  loading: false,
  total: 0,
  current: 1,
  pageSize: 10
})

// 分页调整
const pageChange = (val: any) => {
  searchFun({
    current: val.current,
    pageSize: val.pageSize
  })
}
const resetFun = () => {
  dataMap.current = 1
  dataMap.pageSize = 10
  emit('searchReset')
  if (typeof props.option.searchResetFun === 'function') {
    props.option.searchResetFun()
  }
}

// 查询
const searchFun = async (item: Record<any, any> = {}) => {
  const searchFromParams = search.value.getFieldsValue()
  const params = {
    ...searchFromParams,
    ...item,
    page: item.current || dataMap.current,
    pageSize: item.pageSize || dataMap.pageSize
  }
  if (typeof props.option.searchFunBefore === 'function') {
    const state = props.option.searchFunBefore(params)
    if (!state) return
  }
  const step = props.option.setSearchParams ? props.option.setSearchParams(Object.assign({}, params)) : {}
  dataMap.loading = true
  const searchParams = Object.assign({}, params, step)

  Object.keys(searchParams).forEach((i) => {
    if (searchParams[i] === '') {
      delete searchParams[i]
    }
  })
  const data = await API.query(searchParams)
  console.log('data', data)
  dataMap.loading = false
  if (data && (data.code === 1)) {
    clearSelect()
    dataMap.current = params.page as number
    dataMap.pageSize = params.pageSize as number
    dataList.value = props.option.formatList ? props.option.formatList(data.data) : data?.data?.records
    dataMap.total = data?.data?.total
    // return
  }
  if (data && data.meta && data.items) {
    const { currentPage, itemCount, itemsPerPage, totalItems, totalPages } = data.meta

    clearSelect()
    dataMap.current = currentPage as number
    dataMap.pageSize = itemsPerPage as number
    dataList.value = props.option.formatList ? props.option.formatList(data.items) : data.items
    dataMap.total = totalItems as number
    // return
  }
  console.log('数据设置完成')
  if (typeof props?.option?.searchFunAfter === 'function') {
    props.option.searchFunAfter()
  }
  // message.error(data.msg || lang('查询异常错误'))
}
// 修改
const onUpdate = (row: Record<any, any>) => {
  if (props.option.customEdit) {
    emit('customEdit', row)
  } else {
    showModal('update', Object.assign({}, row))
  }
}
// 删除
const onDelete = (record: any) => {
  Modal.confirm({
    title: lang('确定要删除这条数据吗?'),
    async onOk() {
      const data = await API.remove({ id: record.id })
      if (data.code === 200 || data.code === 0) {
        message.success(lang('删除成功'))
        searchFun()
      } else {
        message.error(data.msg || lang('删除异常错误'))
      }
    }
  })
}
// 查看
const onView = (record: any) => {
  modalDetail.value.showModal('view', Object.assign({}, record))
}
// 自定义测试
const onCreate = () => {
  if (props.option.customAdd) {
    emit('customCreate')
  } else {
    showModal('create', {})
  }
}
// 表单提交成功后
const modalFormSuccess = () => {
  emit('success')
  searchFun()
}
// 显示新增弹窗
const showModal = (type: string, row: Record<any, any>) => {
  modalForm.value.showModal(type, row)
}

onMounted(() => {
  if (props.option.isShowFirst !== false) {
    searchFun()
  }
})

const getSearchParams = () => {
  const searchFromParams = search.value.getFieldsValue()
  const params = {
    ...searchFromParams,
    page: dataMap.current,
    pageSize: dataMap.pageSize
  }
  if (typeof props.option.searchFunBefore === 'function') {
    const state = props.option.searchFunBefore(params)
    if (!state) return false
  }
  const step = props.option.setSearchParams ? props.option.setSearchParams(Object.assign({}, params)) : {}
  const searchParams = Object.assign({}, params, step)

  Object.keys(searchParams).forEach((i) => {
    if (searchParams[i] === '') {
      delete searchParams[i]
    }
  })
  return searchParams
}

defineExpose({
  search,
  searchFun,
  showModal,
  onUpdate,
  onDelete,
  clearSelect,
  showDetailModal: onView,
  getSearchParams,
  searchResetForm: () => {
    search.value.resetForm()
  },
  selectedRowKeys,
  resetSearchFields: (nameList) => {
    if (formRef?.value?.resetFields) {
      formRef.value.resetFields(nameList)
    } else {
      console.error('search表单没有初始化')
    }
  },
  setSearchFieldValue: (fidle, value) => {
    if (search?.value?.setFieldValue) {
      search.value.setFieldValue(fidle, value)
    } else {
      console.error('search表单没有初始化')
    }
  },
  doLayout: () => {
    tableRef.value.updateTableHeight()
  }
})
</script>
<style scoped>
.margin-right{
  margin-right: 10px;
}

:deep(.flex) {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
